import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, Home, Building2, Factory, Briefcase, BookOpen, FileText, Route, Info, Phone, LayoutGrid, Tag, BadgeCheck, HelpCircle, Bot, ListChecks } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';
import { Btn } from './ui';
import { cn } from '@/lib/utils';

const LINK_CLS = 'inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:text-brand dark:text-slate-200 dark:hover:text-brand';

function NavDropdown({ label, items }) {
  return (
    <div className="group relative">
      <button type="button" className={LINK_CLS}>
        {label} <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
      </button>
      <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100">
        <div className="w-[20rem] overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-xl dark:border-white/10 dark:bg-[#121217]">
          {items.map((it) => (
            <Link key={it.to} to={it.to} className="flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-slate-50 dark:hover:bg-white/5">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                <it.icon className="h-[18px] w-[18px]" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-slate-900 dark:text-white">{it.label}</span>
                <span className="mt-0.5 block text-xs leading-snug text-slate-500 line-clamp-2 dark:text-slate-400">{it.desc}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileAcc({ id, label, items, openAcc, setOpenAcc }) {
  const isOpen = openAcc === id;
  return (
    <div className="border-b border-slate-100 dark:border-white/10">
      <button
        type="button"
        onClick={() => setOpenAcc(isOpen ? null : id)}
        className="flex w-full items-center justify-between py-3.5 text-left text-base font-semibold text-slate-900 dark:text-white"
      >
        {label} <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
      </button>
      {isOpen && (
        <div className="pb-3">
          {items.map((it) => (
            <Link key={it.to} to={it.to} className="flex items-center gap-3 rounded-lg px-1 py-2 text-sm text-slate-600 dark:text-slate-300">
              <it.icon className="h-4 w-4 text-brand" /> {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { t } = useLanguage();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openAcc, setOpenAcc] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenAcc(null);
  }, [location.pathname]);

  const platform = [
    { to: '/platform', icon: LayoutGrid, label: `${t('nav.platform')} overview`, desc: 'The full utility bill lifecycle on one system' },
    { to: '/platform/astrid', icon: Bot, label: 'Astrid — AI support agent', desc: 'Your conversational BillPortal specialist' },
    { to: '/platform/onboarding-planner', icon: ListChecks, label: 'Onboarding Planner', desc: 'Plan, assign & track every cutover task to go-live' },
  ];
  const solutions = [
    { to: '/solutions/multifamily', icon: Building2, label: t('nav.multifamily'), desc: t('segments.multifamily.tagline') },
    { to: '/solutions/commercial-industrial', icon: Factory, label: t('nav.commercial'), desc: t('segments.commercial.tagline') },
    { to: '/solutions/energy-brokers', icon: Briefcase, label: t('nav.brokers'), desc: t('segments.brokers.tagline') },
    { to: '/solutions/bill-validation', icon: BadgeCheck, label: 'Bill Validation', desc: 'Data backbone for validation & audit providers' },
  ];
  const resources = [
    { to: '/case-studies', icon: FileText, label: t('nav.caseStudies'), desc: 'Real, anonymised outcomes' },
    { to: '/blog', icon: BookOpen, label: t('nav.blog'), desc: 'Guides & insights' },
    { to: '/how-it-works', icon: Route, label: t('nav.howItWorks'), desc: 'Onboarding to go-live' },
    { to: '/faq', icon: HelpCircle, label: 'FAQ', desc: 'Answers to common questions' },
  ];
  const company = [
    { to: '/about', icon: Info, label: t('nav.about'), desc: 'Mission & team' },
    { to: '/contact', icon: Phone, label: t('nav.contact'), desc: 'Talk to us' },
  ];

  return (
    <header
      className={cn(
        'sticky top-0 z-50 w-full border-b backdrop-blur-md transition-all',
        scrolled
          ? 'border-slate-200/80 bg-white/85 shadow-sm dark:border-white/10 dark:bg-[#0a0a0b]/85'
          : 'border-transparent bg-white/70 dark:bg-[#0a0a0b]/60'
      )}
    >
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-5 sm:px-8">
        <div className="flex items-center gap-1">
          <Logo />
        </div>

        <nav className="hidden items-center gap-0.5 lg:flex">
          <Link to="/" className={LINK_CLS} aria-label="Home" data-testid="nav-home-link"><Home className="h-4 w-4" /></Link>
          <NavDropdown label={t('nav.platform')} items={platform} />
          <NavDropdown label={t('nav.solutions')} items={solutions} />
          <Link to="/pricing" className={LINK_CLS}>{t('nav.pricing')}</Link>
          <NavDropdown label={t('nav.resources')} items={resources} />
          <NavDropdown label={t('nav.company')} items={company} />
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-2 md:flex">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
          <Link to="/login" className="hidden rounded-full px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:text-brand dark:text-slate-200 sm:inline-flex">
            {t('nav.signIn')}
          </Link>
          <Btn to="/demo" size="sm" className="hidden sm:inline-flex">{t('nav.requestDemo')}</Btn>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-700 dark:border-white/15 dark:text-slate-200 lg:hidden"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden">
          <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-slate-200 bg-white px-5 py-3 dark:border-white/10 dark:bg-[#0a0a0b]">
            <Link to="/" className="flex items-center gap-2 border-b border-slate-100 py-3.5 text-base font-semibold text-slate-900 dark:border-white/10 dark:text-white" data-testid="mobile-nav-home-link">
              <Home className="h-4 w-4 text-brand" /> Home
            </Link>
            <MobileAcc id="plat" label={t('nav.platform')} items={platform} openAcc={openAcc} setOpenAcc={setOpenAcc} />
            <MobileAcc id="sol" label={t('nav.solutions')} items={solutions} openAcc={openAcc} setOpenAcc={setOpenAcc} />
            <Link to="/pricing" className="flex items-center gap-2 border-b border-slate-100 py-3.5 text-base font-semibold text-slate-900 dark:border-white/10 dark:text-white">
              <Tag className="h-4 w-4 text-brand" /> {t('nav.pricing')}
            </Link>
            <MobileAcc id="res" label={t('nav.resources')} items={resources} openAcc={openAcc} setOpenAcc={setOpenAcc} />
            <MobileAcc id="com" label={t('nav.company')} items={company} openAcc={openAcc} setOpenAcc={setOpenAcc} />
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <LanguageSwitcher />
                <ThemeToggle />
              </div>
              <Link to="/login" className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('nav.signIn')}</Link>
            </div>
            <Btn to="/demo" size="lg" full className="mt-4">{t('nav.requestDemo')}</Btn>
          </div>
        </div>
      )}
    </header>
  );
}
